import React, { useState, useEffect } from 'react';
import {
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonLabel,
    IonInput,
    IonButton,
    IonRow,
    IonCol,
    IonFooter,
    useIonAlert,
    IonSelect,
    IonSelectOption,
} from '@ionic/react';
import moment from "moment-timezone";
import {actionToAddUpdateUserMembershipStatus} from "../apiHelpers/CommonAction";

export default function AddEditUserMembershipData({
                                                      isOpen,
                                                      onClose,
                                                      setIsLoading,
                                                      dataForEditing,
                                                      reloadUserData
                                                  }) {
    const [form, setForm] = useState({
        membership_type: 'Lifetime',
        start_date: '',
        end_date: '',
        payment_status: 'Success',
    });

    const [errors, setErrors] = useState({});
    const [presentAlert] = useIonAlert();

    // Helper: format JS Date -> "YYYY-MM-DD"
    const formatDate = (date) => {
        if (!date) return '';
        const d = typeof date === 'string' ? new Date(date) : date;
        if (Number.isNaN(d.getTime())) return '';
        return d.toISOString().slice(0, 10);
    };

    // Helper: end date = start date + 1 year - 1 day
    const calcAnnualEnd = (startDateStr) => {
        if (!startDateStr) return '';
        const d = new Date(startDateStr);
        if (Number.isNaN(d.getTime())) return '';
        d.setFullYear(d.getFullYear() + 1);
        d.setDate(d.getDate() - 1);
        return formatDate(d);
    };

    // Pre-fill form for edit OR reset for add
    useEffect(() => {
        if (!isOpen) return;

        if (dataForEditing?.membership_id) {
            const start = dataForEditing.start_date
                ? formatDate(dataForEditing.start_date)
                : '';
            const end = dataForEditing.end_date
                ? formatDate(dataForEditing.end_date)
                : '';

            setForm({
                membership_type: dataForEditing.membership_type || '',
                start_date: start,
                end_date: end,
                payment_status: 'Success',
            });
        } else {
            // Add mode – reset form
            setForm({
                membership_type: 'Lifetime',
                start_date: '',
                end_date: '',
                payment_status: 'Success',
            });
        }
        setErrors({});
    }, [isOpen, dataForEditing]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleMembershipTypeChange = (value) => {
        const todayStr = formatDate(new Date());

        if (value === 'Annual') {
            const start = form.start_date || todayStr;
            const end = calcAnnualEnd(start);
            setForm((prev) => ({
                ...prev,
                membership_type: value,
                start_date: start,
                end_date: end
            }));
        } else if (value === 'Lifetime') {
            setForm((prev) => ({
                ...prev,
                membership_type: value,
                start_date: '',
                end_date: '',
            }));
        } else {
            setForm((prev) => ({
                ...prev,
                membership_type: value,
            }));
        }
    };

    // Auto-update end_date whenever Annual + start_date changes
    useEffect(() => {
        if (form.membership_type !== 'Annual' || !form.start_date) return;

        const newEnd = calcAnnualEnd(form.start_date);
        setForm((prev) => ({
            ...prev,
            end_date: newEnd,
        }));
    }, [form.membership_type, form.start_date]);

    const validate = () => {
        const err = {};
        if (!form.membership_type) {
            err.membership_type = 'Membership type is required';
        }

        if (form.membership_type === 'Annual') {
            if (!form.start_date) {
                err.start_date = 'Start date is required for annual membership';
            }
        }

        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;

        if (setIsLoading) setIsLoading(true);

        const payload = {
            membership_type: form.membership_type,
            start_date: form.membership_type === 'Annual' ? moment(form.start_date).format('YYYY-MM-DD HH:mm:ss') : null,
            end_date: form.membership_type === 'Annual' ? moment(form.end_date).format('YYYY-MM-DD HH:mm:ss') : null,
            membership_id: dataForEditing?.membership_id || null,
            payment_status: form?.payment_status || 'Success',
            member_id: dataForEditing?.id || null,
        };

        actionToAddUpdateUserMembershipStatus(payload).then(() => {
            setIsLoading(false);
            presentAlert({
                message: 'Membership details saved',
                buttons: ['OK'],
            });
            reloadUserData();
        }).catch(() => {
                setIsLoading(false);
                presentAlert({ message: "Something went wrong.", buttons: ['OK'] });
            });
        onClose();
    };

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onClose}
            initialBreakpoint={1}
            breakpoints={[0, 1]}
            handleBehavior="cycle"
        >
            <IonHeader className="job_detail_sheet_header sheet_modal_header">
                <IonToolbar>
                    <IonTitle>
                        {dataForEditing?.membership_id ? 'Edit Membership' : 'Add Membership'}
                    </IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding bottom-2 no_padding_top">
                <div className="rfp_submit_form">
                    {/* Membership Type */}
                    <div className="rfp_submit_form_item">
                        <IonLabel position="floating">Membership Type</IonLabel>
                        <IonSelect
                            className="rfp_form_input"
                            value={form.membership_type}
                            onIonChange={(e) => handleMembershipTypeChange(e.detail.value)}
                        >
                            <IonSelectOption value="Lifetime">Lifetime</IonSelectOption>
                            <IonSelectOption value="Annual">Annual</IonSelectOption>
                        </IonSelect>
                        {errors.membership_type && (
                            <p className="error-text">{errors.membership_type}</p>
                        )}
                    </div>

                    {/* Dates: only for Annual */}
                    {form.membership_type === 'Annual' && (
                        <>
                            {/* Start Date (editable) */}
                            <div className="rfp_submit_form_item">
                                <IonLabel position="floating">Start Date</IonLabel>
                                <IonInput
                                    className="rfp_form_input"
                                    type="date"
                                    value={form.start_date}
                                    onIonInput={(e) =>
                                        handleChange('start_date', e.detail.value)
                                    }
                                />
                                {errors.start_date && (
                                    <p className="error-text">{errors.start_date}</p>
                                )}
                            </div>

                            {/* End Date (auto, readonly) */}
                            <div className="rfp_submit_form_item">
                                <IonLabel position="floating">End Date</IonLabel>
                                <IonInput
                                    className="rfp_form_input"
                                    type="date"
                                    value={form.end_date}
                                    readonly
                                />
                            </div>
                        </>
                    )}
                    <div className="rfp_submit_form_item">
                        <IonLabel position="floating">Payment Status</IonLabel>
                        <IonSelect
                            className="rfp_form_input"
                            value={form.payment_status}
                            onIonChange={(e) =>  handleChange('payment_status', e.detail.value)}
                        >
                            <IonSelectOption value="Success">Success</IonSelectOption>
                            <IonSelectOption value="Pending">Pending</IonSelectOption>
                            <IonSelectOption value="Failed">Failed</IonSelectOption>
                        </IonSelect>
                        {errors.payment_status && (
                            <p className="error-text">{errors.payment_status}</p>
                        )}
                    </div>
                </div>
            </IonContent>

            <IonFooter className="ion-padding">
                <IonToolbar>
                    <IonRow>
                        <IonCol>
                            <IonButton
                                expand="block"
                                className="close_job_detail_button"
                                color="medium"
                                onClick={onClose}
                            >
                                Close
                            </IonButton>
                        </IonCol>
                        <IonCol>
                            <IonButton
                                expand="block"
                                className="apply_job_button"
                                onClick={handleSubmit}
                            >
                                {dataForEditing?.membership_id ? 'Update' : 'Submit'}
                            </IonButton>
                        </IonCol>
                    </IonRow>
                </IonToolbar>
            </IonFooter>
        </IonModal>
    );
}
